import { AxiosError, AxiosRequestConfig } from 'axios';
import { NavigationGuard, RawLocation, Route } from 'vue-router';
import { AuthStrategyType } from '../../tenants/types';
import { AuthStrategy, AuthStrategyOptions } from './types';

/**
 * Acceso con token en la URL (`/item/:id/:token`, `/item/claims/:id/:token`), sin login.
 *
 * Reemplaza al `setToken()` que repetían las páginas del frontend original con acceso por token
 * (`PageItemDetail` y `PageItemWithSinisters`), con el mismo efecto visible:
 * - el guard toma el token del parámetro de la ruta **antes** de crear la página;
 * - `applyAuthorization` lo pone como `Authorization: Bearer <token>` en cada request mientras la ruta activa es
 *   `RouteAccess.UrlToken` (por request: nunca en `Http.defaults`, así una sesión OAuth guardada no lo pisa);
 * - el token no se persiste (vive solo en memoria mientras dura la pestaña);
 * - no hay redirecciones: si el token es inválido, la página muestra su error ("Error al obtener el item");
 * - un 401 no se trata de forma especial (el backend original tampoco lo distingue): se rechaza tal cual.
 */
export class UrlTokenStrategy implements AuthStrategy {
    readonly type = AuthStrategyType.UrlToken;
    readonly supportsLogout = false;

    private token: string | null = null;
    private readonly tokenParam: string;

    constructor(options: AuthStrategyOptions) {
        if (!options.tokenParam) {
            throw new Error('[auth] UrlTokenStrategy requiere `tokenParam` (nombre del parámetro de ruta del token).');
        }
        this.tokenParam = options.tokenParam;
    }

    /** Bearer del token de la URL; sin token, la request sale sin `Authorization`. */
    applyAuthorization(config: AxiosRequestConfig): AxiosRequestConfig {
        config.headers = config.headers || {};
        if (this.token) {
            config.headers.Authorization = `Bearer ${this.token}`;
        } else {
            delete config.headers.Authorization;
        }
        return config;
    }

    getToken(): string | null {
        return this.token;
    }

    isAuthenticated(): boolean {
        return !!this.token;
    }

    handleUnauthorized(error: AxiosError): Promise<any> {
        return Promise.reject(error);
    }

    guard: NavigationGuard = (to, from, next) => {
        this.readTokenFromRoute(to);
        next();
    };

    logout(): RawLocation | null {
        this.token = null;
        return null;
    }

    /** Solo las rutas que declaran el parámetro del token lo actualizan (p. ej. la 404 no lo borra). */
    private readTokenFromRoute(route: Route): void {
        if (Object.prototype.hasOwnProperty.call(route.params, this.tokenParam)) {
            this.token = route.params[this.tokenParam] || null;
        }
    }
}
